// WHICH LANGUAGES RIKSU SHIPS, and the tag a formatter is handed. No messages.
//
// # WHY THIS IS A FILE OF ITS OWN, and it is a BUNDLING decision rather than a tidy one
//
// These four declarations lived at the tail of `catalogue.ts` and were moved here whole,
// with their reasoning, because **rollup places a MODULE in a chunk as a unit.**
//
// `shell/i18n/index.tsx` needs them at runtime, and both applications — the product and
// the public careers surface — reach `index.tsx`. So while they lived beside `en` and `tr`,
// the entire message catalogue was reachable from both entries and landed in the chunk they
// SHARE: 146.3 kB and 1223 keys in two locales, downloaded by an anonymous visitor reading a
// job advert who needs four of them. Measured on the built output, that chunk contained
// "payroll" 129 times, "zimmet" 40, "rapor" 13, "kıdem" 6 and "KVKK" 3 — the internal
// vocabulary of an HR product, served to a stranger.
//
// Nothing about that is fixed by chunking options or by tree-shaking: a module is assigned
// whole, and `catalogue.ts` was reachable from both sides. **Splitting the module is the
// only thing that moves the bytes** — which was established the expensive way, by giving
// careers its own four messages first and measuring the shared chunk go 376.61 kB → 377.00 kB.
// Zero. See `src/careers/messages.ts` and the note on `Messages` in `./index`.
//
// With the split: shared chunk **377.00 → 222.35 kB raw, 119.83 → 70.56 kB gzip**; the
// careers download **123.3 → 74.0 kB gzip**; and the product's own total unchanged to within
// 30 bytes, because the catalogue moves into `app.js` rather than disappearing. `catalogue.ts`
// is now reachable from the PRODUCT entry and from tests, and from nothing else.
//
// The rule this leaves behind: **anything `index.tsx` needs at runtime belongs here, and
// anything that is MESSAGES belongs in `catalogue.ts`.** A value added to the catalogue and
// imported by the runtime silently undoes all of the above, and the only thing that would
// notice is a build.

/// The locales this product ships, as BCP 47 tags — the standard `AGENTS.md` names.
///
/// English first because English is the SOURCE, and the order of this list is the only
/// place that ordering is written down. It is not `en-GB` and that is a decision rather
/// than an inherited default: a bare `en` resolves to **US** conventions, which would
/// render `29 Eki 2026` as `Oct 29, 2026` — month first — in a product whose floor is
/// ISO 8601 for every date and whose `weekdayOf` already refuses the runtime's
/// Sunday-first week for being American. `en-GB` gives `29 Oct 2026`: day first, the
/// same field order as the Turkish and the same order as the ISO form on the wire, and
/// measured identical to `en` on money, decimals and separators (`TRY 1,250.50` either
/// way). So the reader loses nothing and the date stops disagreeing with the rest of
/// the product.
///
/// The TAG is `en-GB` and the CATALOGUE KEY is `en`: `asLocale` narrows on the primary
/// subtag, so a browser asking for `en-US`, `en-AU` or `en` all reach the same messages
/// and all read a date in the one order riksu writes dates in.
export const LOCALES = ["en", "tr"] as const;
export type Locale = (typeof LOCALES)[number];

/// English, because English is the language this product is written in.
///
/// The counter-argument this used to carry — "a product whose default is the language
/// of its engineers rather than its users is one every user has to correct on arrival"
/// — is still true of the TENANT, and it is answered rather than overruled:
/// `appLocale()` consults `navigator.languages` **before** this default, so a Turkish
/// browser still arrives in Turkish and corrects nothing. This is what an *unmatched*
/// browser gets, and an unmatched browser is one riksu knows nothing about — which is
/// the case for serving the source rather than guessing at a market.
///
/// The reachable half of that argument is now honoured properly instead: there is a
/// language switcher (`ui/language.tsx`), so Turkish is CHOSEN and remembered rather
/// than assumed. Before this change there was none, and `rememberLocale` had no caller
/// at all — the browser decided once and nobody could disagree with it.
export const DEFAULT_LOCALE: Locale = "en";

/// The BCP 47 tag a FORMATTER is given, which is not the catalogue key.
///
/// `Intl` is asked for a region and the message catalogue is not: dates, money and
/// collation differ between `en-GB` and `en-US` where the words do not. Keeping the two
/// apart is what lets `LOCALES` stay a short list of languages while every date in the
/// product still comes out day-first. See the note on `LOCALES`.
const FORMATTING: Record<Locale, string> = { en: "en-GB", tr: "tr-TR" };

/// The tag to hand `Intl` for a locale. Never the catalogue key.
export function formattingTag(locale: Locale): string {
  return FORMATTING[locale];
}
